import React, { FC, useEffect, useRef, useState } from "react";
import Select, { components } from "react-select";
import style from "./style.module.scss";
import arrowDownGrey from "../../assets/images/arrowDownGrey.svg";
import arrowUpBlue from "../../assets/images/arrowUpBlue.svg";
import validationError from "../../assets/images/validationError.svg";
import { SmallLoader } from "../Loader";

interface IInputWithSelect {
  id: string;
  label?: string;
  placeholder?: string;
  hasFullWidth?: boolean;
  options: { value: string; label: string }[];
  width?: string;
  marginRight?: string;
  marginBottom?: string;
  withoutMargins?: boolean;
  backgroundColor?: string;
  isRequired?: boolean;
  height?: string;
  isSmallLoaderActive?: boolean;
  isSmallLoaderAvailable?: boolean;
  isDisabled?: boolean;
  ariaLabel?: string;
  onChange: (str: string) => void;
  isTouched?: boolean;
  error?: string;
  onBlur?: (e: React.FocusEvent<any>) => void;
  value: string | undefined;
  setTouched?: (b: boolean) => void;
  menuPlacement?: "auto" | "bottom" | "top";
  filterOption?: (
    option: { value: string; label: string },
    inputValue: string,
  ) => boolean;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  showError?: boolean;
  autoComplete?: string | undefined;
  isReadOnly?: boolean;
  className?: string;
}

export const InputWithSelectV3: FC<IInputWithSelect> = ({
  id,
  label,
  placeholder,
  hasFullWidth,
  options,
  width,
  marginRight,
  withoutMargins = false,
  backgroundColor = "#f6f8fe",
  isRequired = true,
  height,
  isSmallLoaderActive = false,
  isSmallLoaderAvailable = false,
  isDisabled = false,
  marginBottom,
  ariaLabel = "",
  onChange,
  value = "",
  error = "",
  isTouched = false,
  onBlur,
  setTouched,
  menuPlacement = "auto",
  filterOption,
  onKeyDown,
  showError = true,
  autoComplete,
  isReadOnly = false,
  className,
}) => {
  const [isFocused, setIsFocused] = useState<undefined | boolean>(undefined);

  const Input = ({ ...rest }: any) => {
    return (
      <components.Input
        {...rest}
        autoComplete={autoComplete}
        autoFocus={rest.autoFocus}
      />
    );
  };

  useEffect(() => {
    if (id) {
      const input = document.getElementById(id);
      if (input && autoComplete) {
        input.setAttribute("autocomplete", autoComplete);
      }
    }
  }, [id, autoComplete]);

  return (
    <div
      style={{
        width: width?.includes("%")
          ? `${width}`
          : width && !width?.includes("%")
            ? `${width}px`
            : "",
        margin: withoutMargins ? "0px" : "",
        marginRight: marginRight ? `${marginRight}px` : "",
        marginBottom: marginBottom ? `${marginBottom}px` : "",
        height: height ? `${height}px` : "",
        opacity: isDisabled ? 0.5 : 1,
      }}
      className={`${style.input_wrapper} ${
        hasFullWidth ? style.fullWidthInput : ""
      } ${isTouched && error && !value ? style.input_wrapperWithWarning : ""}
      ${className ? className : ""}`}
    >
      {label ? (
        <label
          className={`${style.label} ${
            isTouched && error && !value && !isDisabled
              ? style.labelWithWarning
              : ""
          }`}
          style={{
            display: "flex",
          }}
          htmlFor={id}
        >
          {label}
          {isSmallLoaderAvailable && isSmallLoaderActive ? (
            <SmallLoader loaderDotSize="8" />
          ) : null}
        </label>
      ) : null}
      <div
        style={{
          position: "relative",
        }}
      >
        <Select
          menuPlacement={menuPlacement}
          isDisabled={!options.length || isDisabled || isReadOnly}
          styles={{
            dropdownIndicator: (provided) => {
              return provided;
              // return { ...provided, display: "none" };
            },
            indicatorsContainer: (provided) => {
              return provided;
              // return { ...provided, display: "none" };
            },
            control: () => {
              let border;

              switch (true) {
                case isFocused === true: {
                  border = "1px solid #008bd2";
                  break;
                }
                case (isFocused === false ||
                  isFocused === true ||
                  isFocused === undefined) &&
                  isTouched &&
                  !value &&
                  isRequired: {
                  if (error && !value) {
                    border = "1px solid #de3745";
                    break;
                  }
                  break;
                }

                default: {
                  border = "1px solid #dbe0f0";
                  break;
                }
              }

              return {
                height: height ? `${height}px` : "44px",
                backgroundColor:
                  isTouched && error && !value && !isDisabled
                    ? "rgba(222, 55, 69, 0.05)"
                    : backgroundColor,
                border: border,
                borderRadius: "4px",
                display: "flex",
                alignItems: "center",
                fontFamily: "Nunito",
                fontWeght: 500,
                // fontSize: "14px",
                fontSize: "0.875rem",
                boxShadow:
                  isTouched && error && !value
                    ? "1px 1px 12px rgba(222, 55, 69, 0.08)"
                    : "",
              };
            },
            menu: (provided) => {
              return {
                ...provided,
                marginTop: "0",
                backgroundColor: "#ffffff",
                boxShadow: "1px 1px 12px rgba(40, 54, 64, 0.08)",
                zIndex: 13,
              };
            },
            menuList: (provided) => {
              return {
                ...provided,
                fontFamily: "Nunito",
                fontWeght: 500,
                // fontSize: "14px",
                fontSize: "0.875rem",
                color: "#353B3F",
              };
            },
            option: (provided, state) => {
              return {
                ...provided,
                ":hover": {
                  backgroundColor: state.isSelected
                    ? "rgba(143, 158, 169, 0.1)"
                    : "rgba(143, 158, 169, 0.1)",
                },
                backgroundColor: state.isSelected
                  ? "rgba(143, 158, 169, 0.1)"
                  : state.isFocused
                    ? "#dfecfc !important"
                    : "#ffffff",
                color: state.isSelected ? "#353B3F" : "#353B3F",
                ":active": {
                  backgroundColor: "#B2D4FF !important",
                },
              };
            },
            placeholder: (provided) => {
              return {
                ...provided,
                color:
                  backgroundColor.toLocaleLowerCase() === "#ffffff"
                    ? "#000000A6"
                    : "rgba(143, 158, 169, 0.6)",
              };
            },
          }}
          onChange={(e) => {
            if (typeof e === "string") {
              onChange(e);
            } else if (
              typeof e?.value === "string" ||
              typeof e?.value === "number"
            ) {
              onChange(e?.value);
            }
            setIsFocused(false);
          }}
          onBlur={(e) => {
            if (onBlur) {
              onBlur(e);
            }
            if (setTouched) setTouched(true);
            setIsFocused(false);
          }}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (onKeyDown) onKeyDown(e);
          }}
          options={options}
          isSearchable
          name={id}
          placeholder={placeholder ?? ""}
          value={value ? options.find((el) => el.value === value) : undefined}
          onFocus={() => setIsFocused(true)}
          aria-label={ariaLabel}
          inputId={id}
          blurInputOnSelect
          aria-invalid={
            isTouched && error && !value && !isDisabled ? true : undefined
          }
          aria-labelledby={`${id}-error`}
          filterOption={filterOption}
          components={{
            IndicatorsContainer: (props) => {
              return !isFocused ? (
                <img
                  src={arrowDownGrey}
                  alt="down"
                  className={style.arrowImg}
                  style={
                    {
                      // top: arrowTopLocation ? `${arrowTopLocation}` : "",
                      // transform: arrowTopLocation?.includes("%")
                      //   ? "translateY(-50%)"
                      //   : "",
                    }
                  }
                  // onClick={(e) => e.}
                />
              ) : (
                <img
                  src={arrowUpBlue}
                  alt={"up"}
                  className={style.arrowImg}
                  style={
                    {
                      // top: arrowTopLocation ? `${arrowTopLocation}` : "",
                      // transform: arrowTopLocation?.includes("%")
                      //   ? "translateY(-50%)"
                      //   : "",
                    }
                  }
                />
              );
            },
          }}
        />
        {/* {!isFocused ? (
          <img
            src={arrowDownGrey}
            alt="down"
            className={style.arrowImg}
            style={
              {
                // top: arrowTopLocation ? `${arrowTopLocation}` : "",
                // transform: arrowTopLocation?.includes("%")
                //   ? "translateY(-50%)"
                //   : "",
              }
            }
            // onClick={(e) => e.}
          />
        ) : (
          <img
            src={arrowUpBlue}
            alt={"up"}
            className={style.arrowImg}
            style={
              {
                // top: arrowTopLocation ? `${arrowTopLocation}` : "",
                // transform: arrowTopLocation?.includes("%")
                //   ? "translateY(-50%)"
                //   : "",
              }
            }
          />
        )} */}
      </div>
      <>
        {showError ? (
          <div
            className={`${style.errorMessage} ${
              isTouched && error && !value && !isDisabled
                ? null
                : style.disabledErrorMessage
            }`}
            id={`${id}-error`}
            role={
              isTouched && error && !value && !isDisabled ? "alert" : undefined
            }
          >
            {error}
          </div>
        ) : null}
        <img
          src={validationError}
          alt="Fehler Bild"
          className={`${style.warningImg} ${style.warningImgWithSelect} ${
            isTouched && error && !value && !isDisabled
              ? null
              : style.disabledErrorMessage
          }`}
        />
      </>
    </div>
  );
};
